import React, {useState, useEffect, CSSProperties} from 'react';
import {ProjectVersion} from "../../UserCanister/candid/main.did";

interface CreateVersionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateVersion: (versionData: any) => void;
    versions: ProjectVersion[];
}

const CreateVersionDialog: React.FC<CreateVersionDialogProps> = ({ isOpen, onClose, onCreateVersion, versions }) => {
    // Colors palette - based on ProjectImportOverlay
    const colors = {
        darkBrown: '#452811',
        mediumBrown: '#7D4427',
        lightBrown: '#B35C37',
        cream: '#FCF9EE',
        lightCream: '#FFFEF7',
        neutralGray: '#D3CFBF',
        white: '#FFFFFF',
        accentGold: '#D4AF37',
    };

    const [formData, setFormData] = useState({
        major: 1,
        minor: 0,
        patch: 0,
        prerelease: '',
        build: '',
        description: '',
        releaseNotes: '',
        parentVersionId: ''
    });

    // Initialize with smart defaults when dialog opens
    useEffect(() => {
        if (isOpen && versions && versions.length > 0) {
            const latestVersion = versions[0]?.semanticVersion;
            if (latestVersion) {
                const majorValue = typeof latestVersion.major === 'bigint' ? Number(latestVersion.major) : latestVersion.major;
                const minorValue = typeof latestVersion.minor === 'bigint' ? Number(latestVersion.minor) + 1 : latestVersion.minor + 1;

                setFormData({
                    major: majorValue,
                    minor: minorValue,
                    patch: 0,
                    prerelease: '',
                    build: '',
                    description: '',
                    releaseNotes: '',
                    parentVersionId: versions[0]?.id || ''
                });
            }
        }
    }, [isOpen, versions]);

    // Handle input changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'major' || name === 'minor' || name === 'patch'
                ? parseInt(value) || 0
                : value
        }));
    };

    // Handle form submission
    const handleSubmit = () => {
        onCreateVersion(formData);
        onClose();
    };

    if (!isOpen) return null;

    // Format version for display in dropdown
// Enhanced formatVersion function for CreateVersionDialog

    const formatVersion = (version: ProjectVersion) => {
        if (!version || !version.semanticVersion) return "Unknown";
        const sv = version.semanticVersion;

        // Convert potential BigInt values to numbers
        const major = typeof sv.major === 'bigint' ? Number(sv.major) : sv.major;
        const minor = typeof sv.minor === 'bigint' ? Number(sv.minor) : sv.minor;
        const patch = typeof sv.patch === 'bigint' ? Number(sv.patch) : sv.patch;

        // Base version string
        let result = `${major}.${minor}.${patch}`;

        if (sv.prerelease && typeof sv.prerelease === 'string' && sv.prerelease !== '') {
            result += `-${sv.prerelease}`;
        }

        if (sv.build && typeof sv.build === 'string' && sv.build !== '') {
            result += `+${sv.build}`;
        }
        // Remove any "-+" pattern (empty prerelease followed by build)
        result = result.replace(/-\+/, '+');

        // Get status text
        let statusText = '';
        if (version.status) {
            // Handle both string and variant types
            if (typeof version.status === 'string') {
                statusText = version.status;
            } else if (typeof version.status === 'object') {
                // Extract key name from variant object
                const keys = Object.keys(version.status);
                statusText = keys.length > 0 ? keys[0] : '';
            }
        }

        // Return formatted version with status if available
        return `${result}${statusText ? ` (${statusText})` : ''}`;
    };

    // Styles - following the modern pattern from ProjectImportOverlay
    const overlayStyles: CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(2px)',
        zIndex: 1000,
    };

    const modalStyles: CSSProperties = {
        backgroundColor: colors.cream,
        borderRadius: '12px',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
        width: '600px',
        maxWidth: '90%',
        maxHeight: '90vh',
        overflow: 'hidden',
        border: `1px solid ${colors.neutralGray}`
    };

    const headerStyles: CSSProperties = {
        padding: '20px 24px',
        borderBottom: `1px solid ${colors.neutralGray}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.white,
        position: 'sticky',
        top: 0,
        zIndex: 10
    };

    const headingStyles: CSSProperties = {
        fontSize: '20px',
        fontWeight: 600,
        color: colors.darkBrown,
        margin: 0
    };

    const closeButtonStyles: CSSProperties = {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: colors.mediumBrown,
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        transition: 'background-color 0.2s'
    };

    const bodyStyles: CSSProperties = {
        padding: '24px',
        overflowY: 'auto',
        maxHeight: 'calc(90vh - 140px)' // Allow scrolling within the body
    };

    const fieldGroupStyles: CSSProperties = {
        marginBottom: '20px'
    };

    const labelStyles: CSSProperties = {
        fontWeight: 600,
        color: colors.darkBrown,
        marginBottom: '8px',
        fontSize: '15px',
        display: 'block'
    };

    const optionalTextStyles: CSSProperties = {
        color: colors.lightBrown,
        fontWeight: 'normal',
        fontSize: '13px',
        marginLeft: '4px'
    };

    const inputStyles: CSSProperties = {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '6px',
        border: `1px solid ${colors.neutralGray}`,
        backgroundColor: colors.white,
        fontSize: '14px',
        color: colors.darkBrown,
        boxSizing: 'border-box'
    };

    const textareaStyles: CSSProperties = {
        ...inputStyles,
        minHeight: '100px',
        resize: 'vertical',
        fontFamily: 'inherit'
    };

    const selectStyles: CSSProperties = {
        ...inputStyles,
        appearance: 'auto'
    };

    const footerStyles: CSSProperties = {
        padding: '16px 24px',
        borderTop: `1px solid ${colors.neutralGray}`,
        backgroundColor: colors.lightCream,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        position: 'sticky',
        bottom: 0,
        zIndex: 10
    };

    const cancelButtonStyles: CSSProperties = {
        padding: '10px 20px',
        backgroundColor: 'transparent',
        color: colors.mediumBrown,
        border: `1px solid ${colors.mediumBrown}`,
        borderRadius: '6px',
        fontSize: '15px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    };

    const createButtonStyles: CSSProperties = {
        padding: '10px 20px',
        backgroundColor: colors.mediumBrown,
        color: colors.white,
        border: 'none',
        borderRadius: '6px',
        fontSize: '15px',
        fontWeight: 500,
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        transition: 'background-color 0.2s'
    };

    return (
        <div style={overlayStyles}>
            <div style={modalStyles}>
                {/* Header */}
                <div style={headerStyles}>
                    <h3 style={headingStyles}>Create New Version</h3>
                    <button
                        onClick={onClose}
                        style={closeButtonStyles}
                        onMouseOver={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            target.style.backgroundColor = 'rgba(0,0,0,0.05)';
                        }}
                        onMouseOut={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            target.style.backgroundColor = 'transparent';
                        }}
                    >
                        <svg style={{ height: '20px', width: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div style={bodyStyles}>
                    {/* Version Numbers Section */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '16px',
                        marginBottom: '24px'
                    } as CSSProperties}>
                        <div>
                            <label style={labelStyles}>Major</label>
                            <input
                                type="number"
                                name="major"
                                value={formData.major}
                                onChange={handleChange}
                                min="0"
                                style={inputStyles}
                            />
                        </div>
                        <div>
                            <label style={labelStyles}>Minor</label>
                            <input
                                type="number"
                                name="minor"
                                value={formData.minor}
                                onChange={handleChange}
                                min="0"
                                style={inputStyles}
                            />
                        </div>
                        <div>
                            <label style={labelStyles}>Patch</label>
                            <input
                                type="number"
                                name="patch"
                                value={formData.patch}
                                onChange={handleChange}
                                min="0"
                                style={inputStyles}
                            />
                        </div>
                    </div>

                    {/* Additional Version Info */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '16px',
                        marginBottom: '24px'
                    } as CSSProperties}>
                        <div>
                            <label style={labelStyles}>
                                Prerelease <span style={optionalTextStyles}>(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="prerelease"
                                value={formData.prerelease}
                                onChange={handleChange}
                                placeholder="e.g. alpha.1"
                                style={inputStyles}
                            />
                        </div>
                        <div>
                            <label style={labelStyles}>
                                Build <span style={optionalTextStyles}>(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="build"
                                value={formData.build}
                                onChange={handleChange}
                                placeholder="e.g. build.123"
                                style={inputStyles}
                            />
                        </div>
                    </div>

                    {/* Parent Version */}
                    <div style={fieldGroupStyles}>
                        <label style={labelStyles}>Parent Version</label>
                        <select
                            name="parentVersionId"
                            value={formData.parentVersionId}
                            onChange={handleChange}
                            style={selectStyles}
                        >
                            <option value="">None (Root version)</option>
                            {versions && versions.map(version => (
                                <option key={version.id} value={version.id}>
                                    {formatVersion(version)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Description */}
                    <div style={fieldGroupStyles}>
                        <label style={labelStyles}>
                            Description <span style={optionalTextStyles}>(Optional)</span>
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Brief description of this version"
                            style={textareaStyles}
                            rows={3}
                        />
                    </div>

                    {/* Release Notes */}
                    <div style={fieldGroupStyles}>
                        <label style={labelStyles}>
                            Release Notes <span style={optionalTextStyles}>(Optional)</span>
                        </label>
                        <textarea
                            name="releaseNotes"
                            value={formData.releaseNotes}
                            onChange={handleChange}
                            placeholder="Detailed notes about changes in this version"
                            style={textareaStyles}
                            rows={5}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div style={footerStyles}>
                    <button
                        onClick={onClose}
                        style={cancelButtonStyles}
                        onMouseOver={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            target.style.backgroundColor = 'rgba(125, 68, 39, 0.05)';
                        }}
                        onMouseOut={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            target.style.backgroundColor = 'transparent';
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        style={createButtonStyles}
                        onMouseOver={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            target.style.backgroundColor = '#5C2C18';
                        }}
                        onMouseOut={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            target.style.backgroundColor = colors.mediumBrown;
                        }}
                    >
                        Create Version
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateVersionDialog;